import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
import { Stars as HotIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import indexedDBService from '../../utils/indexedDBService';

interface SoldItem {
  name: string;
  quantity: number;
  revenue: number;
}

const TopSellingItemsWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [topItems, setTopItems] = useState<SoldItem[]>([]);

  useEffect(() => {
    if (currentUser) fetchData();
  }, [currentUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [posRaw, invoicesRaw] = await Promise.all([
        indexedDBService.getAllItems<any>('pos_transactions').catch(() => []),
        indexedDBService.getAllItems<any>('invoices')
      ]);

      const uid = currentUser?.uid;
      const pos = posRaw.filter(p => p.userId === uid);
      const invoices = invoicesRaw.filter(i => i.userId === uid);

      const itemMap = new Map<string, SoldItem>();

      const processItems = (itemsArray: any[]) => {
        if (!itemsArray || !Array.isArray(itemsArray)) return;
        itemsArray.forEach(item => {
          const name = item.itemName || item.name || item.description || 'Unknown Item';
          const qty = parseFloat(item.quantity || 1);
          const rev = parseFloat(item.total || item.amount || (parseFloat(item.price || item.rate || 0) * qty) || 0);

          if (itemMap.has(name)) {
            const prev = itemMap.get(name)!;
            itemMap.set(name, { name, quantity: prev.quantity + qty, revenue: prev.revenue + rev });
          } else {
            itemMap.set(name, { name, quantity: qty, revenue: rev });
          }
        });
      };

      pos.forEach(p => processItems(p.items));
      invoices.forEach(inv => processItems(inv.items));

      const sorted = Array.from(itemMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      setTopItems(sorted);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const maxRevenue = topItems.length > 0 ? topItems[0].revenue : 1;

  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardHeader
        title={<Typography variant="h6" fontWeight="bold">Top Selling Items</Typography>}
        subheader="Based on aggregated revenue"
        avatar={<Avatar sx={{ bgcolor: 'warning.light' }}><HotIcon /></Avatar>}
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : topItems.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No transaction data yet.</Typography>
        ) : (
          <List disablePadding>
            {topItems.map((item, idx) => {
              const percent = (item.revenue / maxRevenue) * 100;
              return (
                <React.Fragment key={idx}>
                  <ListItem alignItems="flex-start" sx={{ px: 0, py: 2, display: 'block' }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="subtitle2" fontWeight="bold">{item.name}</Typography>
                      <Typography variant="subtitle2" fontWeight="900">₹{item.revenue.toLocaleString('en-IN')}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="caption" color="text.secondary">{item.quantity} units sold</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: idx === 0 ? '#4f46e5' : '#818cf8' } }}
                    />
                  </ListItem>
                  {idx < topItems.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default TopSellingItemsWidget;
